import { useModal } from "@ebay/nice-modal-react";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";

import TextLink from "components/DocumentationSnippets/TextLink";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ComboBox from "ds/components/ComboBox";
import ComboBoxDefaultItem from "ds/components/ComboBox/DefaultItem";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import Feedback from "ds/components/Feedback";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import Typography from "ds/components/Typography";
import useAttachPolicy from "shared/Policy/useAttachPolicy";
import useGetAttachablePolicies from "shared/Policy/useGetAttachablePolicies";
import { PolicyType } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import DrawerForm from "ds/components/DrawerNew/Form";

import { GET_POLICIES_REFETCH_QUERIES } from "./constants";

type AttachPolicyFormFields = {
  policyType: PolicyType;
  policy: string;
};

type PoliciesAttachDrawerProps = {
  alreadyAttachedPolicies: Set<string>;
  entityId: string;
  spaceId: string;
};

const PoliciesAttachDrawer = createDrawer(
  ({ alreadyAttachedPolicies, entityId, spaceId }: PoliciesAttachDrawerProps) => {
    const drawer = useModal();
    const attachPolicyForm = useForm<AttachPolicyFormFields>({
      mode: "onChange",
    });

    const {
      control,
      handleSubmit,
      watch,
      formState: { isDirty },
    } = attachPolicyForm;

    const policyType = watch("policyType");

    const { policiesOptions, policyTypesOptions, loading } = useGetAttachablePolicies({
      spaceId,
      alreadyAttachedPolicies,
    });

    const { attachPolicy, loading: isAttaching } = useAttachPolicy(GET_POLICIES_REFETCH_QUERIES);

    const attachablePolicyOptions = useMemo(
      () => policiesOptions.filter((policy) => policy.type === policyType),
      [policiesOptions, policyType]
    );

    const handleClose = () => {
      drawer.hide();
    };

    const handleCancelAttaching = () => {
      handleClose();
    };

    const handleAttach = (formData: AttachPolicyFormFields) => {
      attachPolicy(formData.policy, entityId, () => {
        handleClose();
      });
    };

    const handleFormSubmit = () => {
      handleSubmit(handleAttach)();
    };

    const policyTypesTooltip = (
      <>
        <TooltipModalTitle>Policy type</TooltipModalTitle>
        <TooltipModalBody align="start">
          <Typography tag="p" variant="p-body3">
            Spacelift uses an open-source project called{" "}
            <TextLink href="https://www.openpolicyagent.org/">
              <Typography tag="span" variant="p-body3">
                Open Policy Agent
              </Typography>
            </TextLink>{" "}
            and its rule language,{" "}
            <TextLink href="https://www.openpolicyagent.org/docs/latest/policy-language/">
              <Typography tag="span" variant="p-body3">
                Rego
              </Typography>
            </TextLink>
            , to execute user-defined pieces of code we call Policies at various decision points.
            Policies come in different flavors that we call types, with each type being executed at
            a different decision point.
          </Typography>
          <ReadMoreDocsLink docsUrl={getDocsUrl("/concepts/policy")} />
        </TooltipModalBody>
      </>
    );

    return (
      <DrawerForm blockNavigation isDirty={isDirty} onClose={handleCancelAttaching}>
        <DrawerHeader justify="between">
          <DrawerHeaderTitle title="Attach policy" />
          <DrawerCloseIcon />
        </DrawerHeader>
        <DrawerBody fullHeight>
          <Box direction="column" gap="x-large">
            <Feedback type="banner" variant="info">
              You can only attach policies from the current space and parent spaces that you inherit
              from.
            </Feedback>

            <Controller
              name="policyType"
              control={control}
              rules={{ required: "Policy type is required." }}
              render={({ field, fieldState }) => (
                <ComboBox
                  label="Policy type"
                  error={fieldState.error?.message}
                  items={policyTypesOptions}
                  value={field.value}
                  onChange={field.onChange}
                  tooltipInfo={policyTypesTooltip}
                >
                  {(item) => <ComboBoxDefaultItem id={item.value} label={item.label} />}
                </ComboBox>
              )}
            />

            <Controller
              name="policy"
              control={control}
              rules={{ required: "Policy is required." }}
              render={({ field, fieldState }) => (
                <ComboBox
                  label="Select policy"
                  error={fieldState.error?.message}
                  items={attachablePolicyOptions}
                  isDisabled={!policyType}
                  isLoading={loading}
                  value={field.value}
                  onChange={field.onChange}
                >
                  {(item) => <ComboBoxDefaultItem id={item.value} label={item.label} />}
                </ComboBox>
              )}
            />
          </Box>

          <DrawerFooter>
            <DrawerFooterActions>
              <Button variant="secondary" onPress={handleCancelAttaching} disabled={isAttaching}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                onPress={handleFormSubmit}
                loading={isAttaching}
                disabled={isAttaching}
              >
                Attach
              </Button>
            </DrawerFooterActions>
          </DrawerFooter>
        </DrawerBody>
      </DrawerForm>
    );
  }
);

export const showPoliciesAttachDrawer = createDrawerTrigger(PoliciesAttachDrawer);
